import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../api';
import { employeeService, type Employee } from '../../api/Employee';
import { tripService } from '../../api/trips';
import {
  Users,
  Search,
  RefreshCw,
  ShieldCheck,
  User,
  Phone,
  Briefcase,
  CalendarDays,
  Activity,
  TimerReset,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  StatCard,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '../../components/ui/SharedLayout';

type EmployeeOverview = Employee & {
  totalTrips: number;
  activeTrips: number;
};

const formatDate = (value: Date | string | null | undefined) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const ITEMS_PER_PAGE = 20;

const Funcionarios = () => {
  const { setPageTitle } = useAuth();
  const [employees, setEmployees] = useState<EmployeeOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setPageTitle('Equipe do setor');
  }, [setPageTitle]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [scopedEmployees, scopedTrips] = await Promise.all([
        employeeService.getAllEmployees(),
        tripService.getAll(),
      ]);

      const tripStats = new Map<
        number,
        { totalTrips: number; activeTrips: number }
      >();

      scopedTrips.forEach((trip) => {
        const current = tripStats.get(trip.employee_id) ?? {
          totalTrips: 0,
          activeTrips: 0,
        };

        current.totalTrips += 1;
        if (trip.status === 'aprovada' || trip.status === 'em_andamento') {
          current.activeTrips += 1;
        }

        tripStats.set(trip.employee_id, current);
      });

      setEmployees(
        scopedEmployees.map((employee) => ({
          ...employee,
          totalTrips: tripStats.get(employee.id)?.totalTrips ?? 0,
          activeTrips: tripStats.get(employee.id)?.activeTrips ?? 0,
        })),
      );
    } catch (loadError) {
      setEmployees([]);
      setError(
        getApiErrorMessage(
          loadError,
          'Erro ao carregar a equipe do setor.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return employees;

    return employees.filter((employee) =>
      [
        employee.name,
        employee.cpf,
        employee.cargo,
        employee.email,
        employee.telefone || '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [employees, searchTerm]);

  const totals = useMemo(() => {
    const total = employees.length;
    const ativos = employees.filter((e) => e.status === 'ativo').length;
    const totalViagens = employees.reduce((sum, e) => sum + e.totalTrips, 0);
    const emAndamento = employees.reduce((sum, e) => sum + e.activeTrips, 0);
    return { total, ativos, totalViagens, emAndamento };
  }, [employees]);

  const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredEmployees.length, currentPage, totalPages]);

  const { paginatedEmployees, pageRange } = useMemo(() => {
    const total = filteredEmployees.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = filteredEmployees.slice(startIndex, endIndex);
    return {
      paginatedEmployees: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredEmployees, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'ativo':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <ShieldCheck className="h-3 w-3 stroke-[2]" />
            Ativo
          </Badge>
        );
      case 'inativo':
        return (
          <Badge tone="slate" className="gap-1.5">
            <TimerReset className="h-3 w-3 stroke-[2]" />
            Inativo
          </Badge>
        );
      case 'ferias':
        return (
          <Badge tone="amber" className="gap-1.5">
            <CalendarDays className="h-3 w-3 stroke-[2]" />
            Férias
          </Badge>
        );
      case 'licenca':
        return (
          <Badge tone="red" className="gap-1.5">
            <ShieldCheck className="h-3 w-3 stroke-[2]" />
            Licença
          </Badge>
        );
      default:
        return (
          <Badge tone="slate" className="gap-1.5 capitalize">
            {status}
          </Badge>
        );
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Equipe do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Gestão de colaboradores
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Acompanhe sua equipe
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                do departamento em tempo real
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button variant="secondary" onClick={() => void loadData()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 mb-7">
          <StatCard
            icon={<Users className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0 stroke-[1.8]" />}
            label="Total da Equipe"
            value={totals.total}
            trend={<span>Colaboradores cadastrados</span>}
            variant="deep"
          />
          <StatCard
            icon={<ShieldCheck className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0 stroke-[1.8]" />}
            label="Funcionários Ativos"
            value={totals.ativos}
            trend={<span>Em exercício no setor</span>}
            variant="primary"
          />
          <StatCard
            icon={<Activity className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0 stroke-[1.8]" />}
            label="Viagens Totais"
            value={totals.totalViagens}
            trend={<span>Realizadas pela equipe</span>}
            variant="sky"
          />
          <StatCard
            icon={<TimerReset className="h-5 w-5 min-h-[20px] min-w-[20px] shrink-0 stroke-[1.8]" />}
            label="Em Andamento"
            value={totals.emAndamento}
            trend={<span>Viagens em execução</span>}
            variant="rich"
          />
        </div>

        <SectionCard>
          <CardHeader>
            <div>
              <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                Colaboradores
              </h2>
              <p className="text-[13px] text-slate-500 mt-0.5">
                {loading
                  ? 'Carregando registros...'
                  : pageRange.total === 0
                    ? 'Nenhum colaborador encontrado'
                    : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} colaboradores listados (total cadastrado: ${employees.length})`}
              </p>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-employees"
                placeholder="Pesquisar nome, CPF, cargo, e-mail ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {error && (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando equipe...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Funcionário</Th>
                      <Th>Telefone</Th>
                      <Th>Cargo</Th>
                      <Th>Admissão</Th>
                      <Th>Status</Th>
                      <Th>Viagens</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhum colaborador encontrado
                        </td>
                      </tr>
                    ) : (
                      paginatedEmployees.map((employee) => (
                        <Tr key={employee.id}>
                          <Td>
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                                <User className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-slate-900">
                                  {employee.name}
                                </div>
                                <div className="mt-0.5 text-[12px] text-slate-500 truncate">
                                  {employee.email}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <Phone className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span
                                className="block truncate font-medium text-slate-700 tabular-nums"
                                title={employee.telefone || ''}
                              >
                                {employee.telefone || '-'}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <Briefcase className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span
                                className="block truncate font-medium text-slate-700"
                                title={employee.cargo}
                              >
                                {employee.cargo || '-'}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span className="font-medium text-slate-700 tabular-nums">
                                {formatDate(employee.datadeadimissão)}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            {renderStatusBadge(employee.status)}
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1">
                              <Activity className="h-3.5 w-3.5 stroke-[1.8] text-emerald-500" />
                              <span className="inline-flex items-center rounded-md bg-emerald-50 px-2.5 py-1 text-[12px] font-bold text-emerald-700 ring-1 ring-inset ring-emerald-200 tabular-nums">
                                {employee.totalTrips} realizadas
                              </span>
                            </div>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span
                              key={`dots-${idx}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>
      </div>
    </div>
  );
};

export default Funcionarios;
